import * as React from "react"
import { Link } from "react-router-dom"
import {
  LayoutDashboard,
  Video,
  Theater,
  FileText,
  BarChart3,
  DollarSign,
  CreditCard,
  Settings,
  User,
  HelpCircle,
  Megaphone,
  UsersRound,
  HeartHandshake,
  Clapperboard,
} from "lucide-react"

import { NavMain } from "@/components/nav-main"
import { NavUserSimple } from "@/components/nav-user-simple"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarGroup,
  SidebarGroupContent,
  SidebarSeparator,
} from "@/components/ui/sidebar"
import { useAuth } from "@/contexts/AuthContext"
import { KinoraMark } from "@/components/KinoraMark"
import { StudioSwitcher } from "@/components/StudioSwitcher"
import {
  showTheatreMarketplaceNav,
  theatreMarketplaceNavLabel,
} from "@/lib/partnerType"

const navMain = [
  {
    title: "Dashboard",
    url: "/dashboard",
    icon: LayoutDashboard,
  },
  {
    title: "Content",
    url: "/content",
    icon: Video,
    items: [
      {
        title: "Library",
        url: "/content",
      },
      {
        title: "Movies",
        url: "/content/movies",
      },
      {
        title: "Series",
        url: "/content/series",
      },
      {
        title: "Assets",
        url: "/content/assets",
      },
      {
        title: "New Title",
        url: "/content/movies/new",
      },
    ],
  },
  {
    title: "Collaborations",
    url: "/collaborations",
    icon: HeartHandshake,
  },
  {
    title: "Productions",
    url: "/productions",
    icon: Clapperboard,
  },
  {
    title: "Campaigns",
    url: "/campaigns",
    icon: Megaphone,
  },
  {
    title: "Theatre Marketplace",
    url: "/theatre-marketplace",
    icon: Theater,
  },
  {
    title: "Contracts",
    url: "/contracts",
    icon: FileText,
  },
  {
    title: "Analytics",
    url: "/analytics",
    icon: BarChart3,
  },
  {
    title: "Revenue",
    url: "/revenue",
    icon: DollarSign,
  },
  {
    title: "Billing",
    url: "/billing",
    icon: CreditCard,
  },
  {
    title: "Team",
    url: "/team",
    icon: UsersRound,
  },
]

const navSecondary = [
  {
    title: "Settings",
    url: "/settings",
    icon: Settings,
  },
  {
    title: "Profile",
    url: "/profile",
    icon: User,
  },
]

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { publisher, user } = useAuth()

  const navItems = navMain
    .filter(item => {
      if (item.url === '/theatre-marketplace') {
        return showTheatreMarketplaceNav(publisher?.business_type, publisher?.partner_type)
      }
      return true
    })
    .map(item => {
      if (item.url === '/theatre-marketplace') {
        return {
          ...item,
          title: theatreMarketplaceNavLabel(publisher?.business_type, publisher?.partner_type),
        }
      }
      return item
    })

  const userData = {
    name: publisher?.company_name || user?.email || "Publisher",
    email: user?.email || "",
    avatar: "",
  }

  return (
    <Sidebar collapsible="offcanvas" {...props}>
      <SidebarHeader className="border-b">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">
              <KinoraMark size={24} className="size-6" />
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-display font-semibold">Kinora</span>
                <span className="truncate text-xs text-sidebar-foreground/50 kinora-eyebrow !text-[0.55rem] !tracking-[0.18em]">Studio</span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <StudioSwitcher />
      </SidebarHeader>

      <SidebarContent>
        <NavMain items={navItems} />
        <SidebarSeparator />
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {navSecondary.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton tooltip={item.title} asChild>
                    <Link to={item.url}>
                      {item.icon && <item.icon />}
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
              <SidebarMenuItem>
                <SidebarMenuButton tooltip="Help">
                  <HelpCircle />
                  <span>Help</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t">
        <NavUserSimple user={userData} />
      </SidebarFooter>
    </Sidebar>
  )
}
